import './CustomerList/style.scss'
import React, { useContext, useEffect, useState } from 'react'
import {
    FiDownload, FiUser, FiUsers, FiTrendingUp, FiX,
} from 'react-icons/fi'
import {
    Box, Flex, Text, tokens,
    Button, SearchField, FilterDropdown, Toolbar,
    InstitutionTable, TableColumn, TablePagination,
} from 'src/componentv2'
import { fontBold, fontMedium } from 'src/model/v2/interface/constants'
import CustomerApiContext, {
    CustomerListItem,
    CustomerMetrics,
    CbpProfileSummary,
    CbpProfileOnboardingStatus,
    UpgradeRequestListItem,
    UpgradeRequestDetail,
    CustomerApiProvider,
} from 'src/provider/API/v2/call-service/Customer/index'
import dateTime from 'src/utils/dateTime'
import { StatusTag } from './components/StatusTag'
import { CustomerDetail } from './CustomerDetail'
import { AccountUpgradeDetail } from './AccountUpgradeDetail'
import type { CustomerView } from './CustomerList/types'

export interface CustomerManagementProps {
    view: CustomerView
}

const PAGE_SIZE = 6

type ListMeta = { total: number; pageCount: number; perPage: number; currentPageNumber: number }

function parseFilterValue(filters: string[], category: string): string | undefined {
    const match = filters.find((f) => f.startsWith(`${category}:`))
    return match ? match.split(':')[1] : undefined
}

function nextFilters(current: string[], nextValues: string[]): string[] {
    const added = nextValues.find((v) => !current.includes(v))
    if (!added) return nextValues
    const category = added.split(':')[0]
    return nextValues.filter((v) => v === added || v.split(':')[0] !== category)
}

function customerName(c: CustomerListItem) {
    const parts = [c.firstName, c.lastName].filter(Boolean)
    return parts.length ? parts.join(' ') : c.username || `Profile #${c.profileId}`
}

function accountStatusOf(c: CustomerListItem) {
    return c.isAccountLocked ? 'Deactivated' : 'Active'
}

function onboardingStatusOf(c: CustomerListItem) {
    return c.isOnboardingCompleted === true ? 'Completed' : 'Pending'
}

function formatJoinDate(iso: string) {
    const d = dateTime.parseUtc(iso)
    if (!d) return '—'
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}


function CustomerAnalyticsBanner({ metrics }: { metrics: CustomerMetrics | null }) {
    const total = metrics?.totalCustomers ?? 0
    const newCount = metrics?.totalNewCustomers ?? 0
    const existing = metrics?.totalExistingCustomers ?? 0
    const newThisWeek = metrics?.newCustomersThisWeek ?? 0

    return (
        <Box bgColor="#101828" radius="1.6" style={{ padding: '2.4rem' }}>
            <Flex justify="space-between" items="center" wrap="wrap" gap="1.6">
                <Flex direction="column" gap="0.8" items="flex-start">
                    <Flex items="center" gap="0.8">
                        <FiUsers size={18} color="#EAECF0" />
                        <Text fontSize="1.4" color="#EAECF0">Total Number of Customers</Text>
                    </Flex>
                    <Flex items="center" gap="1.2" wrap="wrap">
                        <Text fontSize="2.8" style={{ ...fontBold, lineHeight: 1.2 }} color="#FFFFFF">
                            {total.toLocaleString()}
                        </Text>
                        {newThisWeek > 0 && (
                            <Flex items="center" gap="0.4">
                                <FiTrendingUp size={16} color="#12B76A" />
                                <Text fontSize="1.4" style={fontMedium} color="#12B76A">{newThisWeek.toLocaleString()}</Text>
                                <Text fontSize="1.4" style={fontMedium} color="#FFFFFF">This week</Text>
                            </Flex>
                        )}
                    </Flex>
                </Flex>

                <Flex items="center" gap="1.6">
                    <Flex direction="column" gap="0.4" items="flex-start">
                        <Text fontSize="1.2" color="#FCFCFD">New Customers</Text>
                        <Text fontSize="1.8" style={fontBold} color="#5EC3BD">{newCount.toLocaleString()}</Text>
                    </Flex>
                    <Box style={{ width: 1, height: 48, background: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />
                    <Flex direction="column" gap="0.4" items="flex-start">
                        <Text fontSize="1.2" color="#FCFCFD">Existing Customers</Text>
                        <Text fontSize="1.8" style={fontBold} color="#36BFFA">{existing.toLocaleString()}</Text>
                    </Flex>
                </Flex>
            </Flex>
        </Box>
    )
}

// ── Customer List ───────────────────────────────────────────────────────
const CUSTOMER_FILTER_OPTIONS = [
    { label: 'Active', value: 'status:Active' },
    { label: 'Locked', value: 'status:Locked' },
    { label: 'Onboarding Complete', value: 'onboarding:Completed' },
    { label: 'Onboarding Pending', value: 'onboarding:Pending' },
]

function CustomerList() {
    const { getCustomers, getCustomerMetrics } = useContext(CustomerApiContext)

    const [search, setSearch] = useState('')
    const [filters, setFilters] = useState<string[]>([])
    const [page, setPage] = useState(1)
    const [detailProfileId, setDetailProfileId] = useState<number | null>(null)

    const [items, setItems] = useState<CustomerListItem[]>([])
    const [meta, setMeta] = useState<ListMeta | null>(null)
    const [metrics, setMetrics] = useState<CustomerMetrics | null>(null)

    const statusFilter = parseFilterValue(filters, 'status')
    const onboardingFilter = parseFilterValue(filters, 'onboarding')

    // List refetches whenever search/filters/page change.
    useEffect(() => {
        let cancelled = false

        ;(async () => {
            const result = await getCustomers({
                Search: search || undefined,
                Status: statusFilter,
                OnboardingMethod: onboardingFilter,
                PageLimit: PAGE_SIZE,
                PageNumber: page,
            })
            if (!cancelled) {
                setItems(result.items)
                setMeta(result.meta)
            }
        })()

        return () => { cancelled = true }
    }, [search, statusFilter, onboardingFilter, page])

    // Banner metrics are independent of search/filters/pagination — fetched
    // once on mount from the dedicated metrics endpoint.
    useEffect(() => {
        let cancelled = false

        ;(async () => {
            const result = await getCustomerMetrics()
            if (!cancelled) setMetrics(result)
        })()

        return () => { cancelled = true }
    }, [])

    // Any filter change resets to page 1 — same rule InstitutionList applies.
    useEffect(() => { setPage(1) }, [search, filters])

    if (detailProfileId !== null) {
        return <CustomerDetail profileId={detailProfileId} onBack={() => setDetailProfileId(null)} />
    }

    const columns: TableColumn<CustomerListItem>[] = [
        {
            key: 'sn',
            title: 'S/N',
            render: (row) => {
                // Calculate serial number based on page offset and current row index
                const index = items.findIndex((item) => item.profileId === row.profileId)
                const currentPage = meta?.currentPageNumber ?? page
                const serialNum = (currentPage - 1) * PAGE_SIZE + index + 1

                return (
                    <Text fontSize="1.3" color={tokens.textDark}>
                        {serialNum}
                    </Text>
                )
            },
        },
        {
            key: 'customerId',
            title: 'Customer ID',
            render: (row) => (
                <Text fontSize="1.4" style={fontMedium} color={tokens.primary}>
                    {row.customerId || '—'}
                </Text>
            ),
        },
        {
            key: 'name',
            title: 'Name',
            render: (row) => {
                const fullName = [row.firstName, row.lastName].filter(Boolean).join(' ')
                return (
                    <Text fontSize="1.4" style={{ ...fontMedium, whiteSpace: 'nowrap' }} color={tokens.textDark}>
                        {fullName || '—'}
                    </Text>
                )
            },
        },
        {
            key: 'username',
            title: 'Username',
            render: (row) => <Text fontSize="1.3" color={tokens.textDark} style={{ whiteSpace: 'nowrap' }}>{row.username || '—'}</Text>,
        },
        {
            key: 'primaryAccountNumber',
            title: 'Account Number',
            render: (row) => <Text fontSize="1.3" color={tokens.textDark} style={{ whiteSpace: 'nowrap' }}>{row.primaryAccountNumber || '—'}</Text>,
        },
        {
            key: 'accountStatus',
            title: 'Status',
            render: (row) => <StatusTag status={accountStatusOf(row)} />
        },
        {
            key: 'joinDate',
            title: 'Join Date',
            render: (row) => <Text fontSize="1.3" color={tokens.textMuted} style={{ whiteSpace: 'nowrap' }}>{formatJoinDate(row.joinDate)}</Text>,
        },
        {
            key: 'actions',
            title: '',
            width: '13rem',
            render: (row) => (
                <Text
                    fontSize="1.4"
                    fontWeight="600"
                    color={tokens.primary}
                    style={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
                    onClick={() => setDetailProfileId(row.profileId)}
                >
                    View Details
                </Text>
            ),
        },
    ]
    return (
        <div className="customer-mgmt__container">
            <Box style={{ marginBottom: '1.6rem' }}>
                <CustomerAnalyticsBanner metrics={metrics} />
            </Box>

            <Box className="customer-mgmt__page-header">
                <Text fontSize="2.4" style={fontBold} color={tokens.textDark}>Customer List</Text>
                <Text topMargin="0.4" fontSize="1.4" color={tokens.textMuted}>
                    Manage all registered customers across the platform
                </Text>
            </Box>

            <Box topMargin="2.4" bgColor={tokens.white} borderColor={tokens.border} radius="1.2" shadow={tokens.shadowCard} style={{ padding: '0 1.6rem' }}>
                <Toolbar
                    left={(
                        <>
                            <SearchField width="28rem" value={search} onChange={setSearch} placeholder="Search" />
                            <FilterDropdown
                                label="Filters"
                                options={CUSTOMER_FILTER_OPTIONS}
                                value={filters}
                                onChange={(v: string[]) => setFilters(nextFilters(filters, v))}
                            />
                            {(search || filters.length > 0) && (
                                <Button variant="ghost" onClick={() => { setSearch(''); setFilters([]) }}>
                                    <FiX size={14} style={{ marginRight: '0.4rem' }} />
                                    Clear filters
                                </Button>
                            )}
                        </>
                    )}
                    right={(
                        <Button variant="outline">
                            <FiDownload size={14} style={{ marginRight: '0.6rem' }} />
                            Export
                        </Button>
                    )}
                />
            </Box>

            <Box topMargin="1.6" bgColor={tokens.white} borderColor={tokens.border} radius="1.2" shadow={tokens.shadowCard} style={{ overflow: 'hidden' }}>
                <div className="customer-mgmt__table-scroll">
                    <InstitutionTable
                        columns={columns}
                        data={items}
                        emptyText={(
                            <Flex direction="column" items="center" gap="0.8" style={{ padding: '2.4rem 0' }}>
                                <FiUser size={36} color={tokens.textMuted} style={{ opacity: 0.4 }} />
                                <Text fontSize="1.4" color={tokens.textMuted}>No customers found</Text>
                            </Flex>
                        )}
                    />
                </div>

                <div style={{ borderTop: `1px solid ${tokens.border}` }} />

                <Box padding="1.6">
                    <TablePagination
                        page={meta?.currentPageNumber ?? page}
                        total={meta?.total ?? 0}
                        pageSize={meta?.perPage ?? PAGE_SIZE}
                        onPageChange={setPage}
                    />
                </Box>
            </Box>
        </div>
    )
}

const CBP_STATUS_LABELS: Record<CbpProfileOnboardingStatus, string> = {
    [CbpProfileOnboardingStatus.Submitted]: 'Submitted',
    [CbpProfileOnboardingStatus.IndemnityRejected]: 'Indemnity Rejected',
    [CbpProfileOnboardingStatus.IndemnityAccepted]: 'Indemnity Accepted',
    [CbpProfileOnboardingStatus.OngoingAmlScreening]: 'Ongoing AML Screening',
    [CbpProfileOnboardingStatus.AmlScreeningPassed]: 'AML Screening Passed',
    [CbpProfileOnboardingStatus.AmlScreeningFailed]: 'AML Screening Failed',
    [CbpProfileOnboardingStatus.Approved]: 'Approved',
}

const CBP_FILTER_OPTIONS = Object.entries(CBP_STATUS_LABELS).map(([value, label]) => ({
    label,
    value: `status:${value}`,
}))

function cbpFullName(c: CbpProfileSummary) {
    return [c.firstName, c.lastName].filter(Boolean).join(' ') || '—'
}

function CbpCustomerList() {
    const { getCbpCustomers } = useContext(CustomerApiContext)

    const [search, setSearch] = useState('')
    const [filters, setFilters] = useState<string[]>([])
    const [page, setPage] = useState(1)

    const [items, setItems] = useState<CbpProfileSummary[]>([])
    const [meta, setMeta] = useState<ListMeta | null>(null)

    const statusFilter = parseFilterValue(filters, 'status')

    useEffect(() => {
        let cancelled = false

        ;(async () => {
            const result = await getCbpCustomers({
                CustomerId: search || undefined,
                Status: statusFilter !== undefined ? (Number(statusFilter) as CbpProfileOnboardingStatus) : undefined,
                PageLimit: PAGE_SIZE,
                PageNumber: page,
            })
            if (!cancelled) {
                setItems(result.items)
                setMeta(result.meta)
            }
        })()

        return () => { cancelled = true }
    }, [search, statusFilter, page])

    useEffect(() => { setPage(1) }, [search, filters])

    const columns: TableColumn<CbpProfileSummary>[] = [
        {
            key: 'customerId',
            title: 'Customer ID',
            render: (row) => <Text fontSize="1.4" style={fontMedium} color={tokens.primary}>{row.customerId || '—'}</Text>,
        },
        {
            key: 'name',
            title: 'Full Name',
            render: (row) => <Text fontSize="1.4" style={{ ...fontMedium, whiteSpace: 'nowrap' }} color={tokens.textDark}>{cbpFullName(row)}</Text>,
        },
        {
            key: 'internationalCustomerId',
            title: 'International Customer ID',
            render: (row) => <Text fontSize="1.3" color={tokens.textDark} style={{ whiteSpace: 'nowrap' }}>{row.internationalCustomerId || '—'}</Text>,
        },
        {
            key: 'nationality',
            title: 'Nationality',
            render: (row) => <Text fontSize="1.3" color={tokens.textDark}>{row.nationality || '—'}</Text>,
        },
        {
            key: 'phoneNumber',
            title: 'Phone Number',
            render: (row) => <Text fontSize="1.3" color={tokens.textDark} style={{ whiteSpace: 'nowrap' }}>{row.phoneNumber || '—'}</Text>,
        },
        {
            key: 'primaryAccountNumber',
            title: 'Account Number',
            render: (row) => <Text fontSize="1.3" color={tokens.textDark} style={{ whiteSpace: 'nowrap' }}>{row.primaryAccountNumber || '—'}</Text>,
        },
        {
            key: 'status',
            title: 'Status',
            render: (row) => <StatusTag status={CBP_STATUS_LABELS[row.status] ?? 'Unknown'} />,
        },
    ]

    return (
        <div className="customer-mgmt__container">
            <Box className="customer-mgmt__page-header">
                <Text fontSize="2.4" style={fontBold} color={tokens.textDark}>CBP Customer List</Text>
                <Text topMargin="0.4" fontSize="1.4" color={tokens.textMuted}>
                    Corporate Banking Platform customers and their onboarding status
                </Text>
            </Box>

            <Box topMargin="2.4" bgColor={tokens.white} borderColor={tokens.border} radius="1.2" shadow={tokens.shadowCard} style={{ padding: '0 1.6rem' }}>
                <Toolbar
                    left={(
                        <>
                            <SearchField width="28rem" value={search} onChange={setSearch} placeholder="Search by Customer ID" />
                            <FilterDropdown
                                label="Filters"
                                options={CBP_FILTER_OPTIONS}
                                value={filters}
                                onChange={(v: string[]) => setFilters(nextFilters(filters, v))}
                            />
                            {(search || filters.length > 0) && (
                                <Button variant="ghost" onClick={() => { setSearch(''); setFilters([]) }}>
                                    <FiX size={14} style={{ marginRight: '0.4rem' }} />
                                    Clear filters
                                </Button>
                            )}
                        </>
                    )}
                    right={(
                        <Button variant="outline">
                            <FiDownload size={14} style={{ marginRight: '0.6rem' }} />
                            Export
                        </Button>
                    )}
                />
            </Box>

            <Box topMargin="1.6" bgColor={tokens.white} borderColor={tokens.border} radius="1.2" shadow={tokens.shadowCard} style={{ overflow: 'hidden' }}>
                <div className="customer-mgmt__table-scroll">
                    <InstitutionTable columns={columns} data={items} emptyText="No CBP customers found" />
                </div>

                <div style={{ borderTop: `1px solid ${tokens.border}` }} />

                <Box padding="1.6">
                    <TablePagination
                        page={meta?.currentPageNumber ?? page}
                        total={meta?.total ?? 0}
                        pageSize={meta?.perPage ?? PAGE_SIZE}
                        onPageChange={setPage}
                    />
                </Box>
            </Box>
        </div>
    )
}


const UPGRADE_FILTER_OPTIONS = [
    { label: 'Submitted', value: 'status:Submitted' },
    { label: 'Indemnity Accepted', value: 'status:IndemnityAccepted' },
    { label: 'Indemnity Rejected', value: 'status:IndemnityRejected' },
    { label: 'Ongoing AML Screening', value: 'status:OngoingAmlScreening' },
    { label: 'AML Screening Passed', value: 'status:AmlScreeningPassed' },
    { label: 'AML Screening Failed', value: 'status:AmlScreeningFailed' },
    { label: 'Approved', value: 'status:Approved' },
    { label: 'Rejected', value: 'status:Rejected' },
]

function AccountUpgrade() {
    const { getUpgradeRequests, getUpgradeRequest, updateUpgradeDecision } = useContext(CustomerApiContext)

    const [search, setSearch] = useState('')
    const [filters, setFilters] = useState<string[]>([])
    const [page, setPage] = useState(1)

    const [items, setItems] = useState<UpgradeRequestListItem[]>([])
    const [meta, setMeta] = useState<ListMeta | null>(null)

    const [selectedRequest, setSelectedRequest] = useState<UpgradeRequestDetail | null>(null)
    const [detailLoading, setDetailLoading] = useState(false)

    const statusFilter = parseFilterValue(filters, 'status')

    const fetchList = async () => {
        const result = await getUpgradeRequests({
            Search: search || undefined,
            Status: statusFilter,
            PageLimit: PAGE_SIZE,
            PageNumber: page,
        })
        setItems(result.items)
        setMeta(result.meta)
    }

    useEffect(() => {
        let cancelled = false

        ;(async () => {
            const result = await getUpgradeRequests({
                Search: search || undefined,
                Status: statusFilter,
                PageLimit: PAGE_SIZE,
                PageNumber: page,
            })
            if (!cancelled) {
                setItems(result.items)
                setMeta(result.meta)
            }
        })()

        return () => { cancelled = true }
    }, [search, statusFilter, page])

    useEffect(() => { setPage(1) }, [search, filters])

    const openDetail = async (row: UpgradeRequestListItem) => {
        if (!row.accountNumber || !row.requestedAt) return
        setDetailLoading(true)
        const result = await getUpgradeRequest(row.accountNumber, row.requestedAt)
        setDetailLoading(false)
        if (result) setSelectedRequest(result)
    }

    const handleDecision = async (accountNumber: string, requestedAt: string, isApproved: boolean, reason?: string) => {
        const result = await updateUpgradeDecision({ accountNumber, requestedAt, isApproved, reason: reason ?? null })
        if (result) {
            setSelectedRequest(null)
            await fetchList()
        }
    }

    if (selectedRequest) {
        return (
            <AccountUpgradeDetail
                request={selectedRequest}
                onBack={() => setSelectedRequest(null)}
                onDecision={handleDecision}
            />
        )
    }

    const columns: TableColumn<UpgradeRequestListItem>[] = [
        { key: 'sn', title: 'S/N', render: (row) => <Text fontSize="1.3" color={tokens.textDark}>{row.sn}</Text> },
        { key: 'accountNumber', title: 'Account Number', render: (row) => <Text fontSize="1.4" style={fontMedium} color={tokens.primary}>{row.accountNumber || '—'}</Text> },
        { key: 'customerID', title: 'Customer ID', render: (row) => <Text fontSize="1.4" style={{ ...fontMedium, whiteSpace: 'nowrap' }} color={tokens.textDark}>{row.customerID || '—'}</Text> },
        { key: 'requestedAt', title: 'Requested At', render: (row) => <Text fontSize="1.3" color={tokens.textMuted} style={{ whiteSpace: 'nowrap' }}>{formatJoinDate(row.requestedAt)}</Text> },
        { key: 'status', title: 'Status', render: (row) => <StatusTag status={row.status ?? 'Unknown'} /> },
        {
            key: 'actions',
            title: '',
            width: '13rem',
            render: (row) => (
                <Text
                    fontSize="1.4"
                    fontWeight="600"
                    color={tokens.primary}
                    style={{ whiteSpace: 'nowrap', cursor: detailLoading ? 'default' : 'pointer' }}
                    onClick={() => !detailLoading && openDetail(row)}
                >
                    View Details
                </Text>
            ),
        },
    ]

    return (
        <div className="customer-mgmt__container">
            <Box className="customer-mgmt__page-header">
                <Text fontSize="2.4" style={fontBold} color={tokens.textDark}>Account Upgrade</Text>
                <Text topMargin="0.4" fontSize="1.4" color={tokens.textMuted}>
                    Review and process customer account tier upgrade requests
                </Text>
            </Box>

            <Box topMargin="2.4" bgColor={tokens.white} borderColor={tokens.border} radius="1.2" shadow={tokens.shadowCard} style={{ padding: '0 1.6rem' }}>
                <Toolbar
                    left={(
                        <>
                            <SearchField width="28rem" value={search} onChange={setSearch} placeholder="Search" />
                            <FilterDropdown
                                label="Filters"
                                options={UPGRADE_FILTER_OPTIONS}
                                value={filters}
                                onChange={(v: string[]) => setFilters(nextFilters(filters, v))}
                            />
                            {(search || filters.length > 0) && (
                                <Button variant="ghost" onClick={() => { setSearch(''); setFilters([]) }}>
                                    <FiX size={14} style={{ marginRight: '0.4rem' }} />
                                    Clear filters
                                </Button>
                            )}
                        </>
                    )}
                    right={(
                        <Button variant="outline">
                            <FiDownload size={14} style={{ marginRight: '0.6rem' }} />
                            Export
                        </Button>
                    )}
                />
            </Box>

            <Box topMargin="1.6" bgColor={tokens.white} borderColor={tokens.border} radius="1.2" shadow={tokens.shadowCard} style={{ overflow: 'hidden' }}>
                <div className="customer-mgmt__table-scroll">
                    <InstitutionTable columns={columns} data={items} emptyText="No upgrade requests found" />
                </div>

                <div style={{ borderTop: `1px solid ${tokens.border}` }} />

                <Box padding="1.6">
                    <TablePagination
                        page={meta?.currentPageNumber ?? page}
                        total={meta?.total ?? 0}
                        pageSize={meta?.perPage ?? PAGE_SIZE}
                        onPageChange={setPage}
                    />
                </Box>
            </Box>
        </div>
    )
}

// ── Root component ──────────────────────────────────────────────────────
export function CustomerManagement({ view }: CustomerManagementProps) {
    return (
        <CustomerApiProvider>
            <div className="customer-mgmt">
                {view === 'list' && <CustomerList />}
                {view === 'cbp-list' && <CbpCustomerList />}
                {view === 'account-upgrade' && <AccountUpgrade />}
            </div>
        </CustomerApiProvider>
    )
}

export default CustomerManagement